import { useMemo, useState } from 'react';
import {
  BOARD_LEGEND,
  CELLS,
  COLOR_META,
  COLOR_ORDER,
  RING_CELL_SIZE,
  RING_POSITIONS,
  baseSlotPoint,
  cellAt,
  trackIndexForProgress,
} from '@shared/game/board';
import {
  GOAL_PROGRESS,
  type CellInfo,
  type GamePlayer,
  type GameState,
  type LegalMove,
  type SeatColor,
} from '@shared/game/types';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { Token3DLayer, type Token3DPiece } from './Token3DLayer';
import { unitRole } from '../lib/format';

interface BoardProps {
  game: GameState;
  myPlayerId: string;
  onMove: (pieceId: string) => void;
  animations: boolean;
  movingPieceId?: string | null;
  safePieceId?: string | null;
}

interface RenderPiece {
  player: GamePlayer;
  pieceIndex: number;
  legal: LegalMove | undefined;
  x: number;
  y: number;
}

function cellDescription(cell: CellInfo, effectsEnabled: boolean): { title: string; text: string } {
  const base = BOARD_LEGEND.find((entry) => entry.kind === cell.kind);
  if (!base) return { title: `Celda ${cell.index + 1}`, text: 'Plataforma de tránsito sin anomalía detectada.' };
  if (cell.kind === 'start') {
    return {
      title: `Salida ${COLOR_META[cell.color ?? 'coral'].label}`,
      text: 'Plataforma segura del escuadrón. Con un 5 puedes desplegar una unidad desde el muelle.',
    };
  }
  if (cell.kind === 'safe') {
    return { title: 'Zona segura', text: 'Ninguna unidad puede ser capturada aquí. No se forman bloqueos.' };
  }
  if (!effectsEnabled) {
    return {
      title: base.title,
      text: 'Las anomalías están desactivadas: la celda funciona como una plataforma normal.',
    };
  }
  const color = cell.color ? COLOR_META[cell.color].label : '';
  return {
    title: `${base.title}${color ? ` · ${color}` : ''}`,
    text: base.description,
  };
}

function FactionCrest({ sigil, size = 1 }: { sigil: 'blade' | 'reactor' | 'circuit' | 'void'; size?: number }) {
  if (sigil === 'blade') {
    return (
      <g transform={`scale(${size})`} fill="currentColor">
        <path d="M0-11 4-3l7-2-5 6 6 4-8 1-4 6-4-6-8-1 6-4-5-6 7 2 4-8Z" />
        <path d="M0-4 2 1l4 1-4 2-2 4-2-4-4-2 4-1 2-5Z" fill="#ffe7a8" opacity=".7" />
      </g>
    );
  }
  if (sigil === 'reactor') {
    return (
      <g transform={`scale(${size})`} fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle r="5" fill="currentColor" fillOpacity=".22" />
        <circle r="9" strokeDasharray="2 4" />
        {Array.from({ length: 6 }, (_, index) => {
          const angle = (Math.PI * 2 * index) / 6;
          return <path key={index} d={`M${Math.cos(angle) * 7} ${Math.sin(angle) * 7}L${Math.cos(angle) * 11} ${Math.sin(angle) * 11}`} />;
        })}
      </g>
    );
  }
  if (sigil === 'circuit') {
    return (
      <g transform={`scale(${size})`} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-9 7V-2h6v-6h6M9 7V1H4v-5" />
        <circle cx="-9" cy="7" r="2" fill="currentColor" />
        <circle cx="9" cy="7" r="2" fill="currentColor" />
        <circle cx="3" cy="-8" r="2" fill="currentColor" />
        <path d="M-3 8h6" opacity=".5" />
      </g>
    );
  }
  return (
    <g transform={`scale(${size})`} fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle r="9" strokeDasharray="1 4" />
      <path d="M0-8C5-3 6 2 3 7 0 9-4 7-4 3c0-2 2-4 4-6 2 2 2 5 0 6" fill="currentColor" fillOpacity=".16" />
      <circle cx="9" cy="-5" r="1.5" fill="currentColor" />
    </g>
  );
}

function CellGlyph({ cell }: { cell: CellInfo }) {
  if (cell.kind === 'start') {
    return (
      <g className="faction-glyph">
        <FactionCrest sigil={COLOR_META[cell.color ?? 'coral'].sigil} size={0.78} />
      </g>
    );
  }
  if (cell.kind === 'safe') {
    return (
      <g className="glyph-safe" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
        <path d="m0-10 9 5v5C9 5.5 6 9 0 11c-6-2-9-5.5-9-11V-5l9-5Z" fill="currentColor" fillOpacity=".08" />
        <path d="M-4 0h8M0-4v8" opacity=".7" />
        <circle r="12" strokeDasharray="1 4" opacity=".3" />
      </g>
    );
  }
  if (cell.kind === 'shortcut') {
    return (
      <g className="glyph-portal" fill="none" stroke="currentColor" strokeWidth="1.25">
        <circle r="9.5" strokeDasharray="2 3" opacity=".45" />
        <ellipse rx="3.3" ry="8.5" className="glyph-portal__ring" />
        <ellipse rx="8.5" ry="3.3" className="glyph-portal__ring glyph-portal__ring--alt" />
        <circle r="2" fill="currentColor" stroke="none" className="glyph-portal__core" />
        <path d="M-10-5 10 5M-10 5 10-5" opacity=".18" />
      </g>
    );
  }
  if (cell.kind === 'event') {
    return (
      <g className="glyph-event">
        <rect x="-10" y="-5" width="20" height="15" rx="3" fill="currentColor" fillOpacity=".14" stroke="currentColor" strokeWidth="1.3" />
        <path d="M-10-5c0-5 4-8 10-8s10 3 10 8" fill="currentColor" fillOpacity=".18" stroke="currentColor" strokeWidth="1.3" />
        <path d="M-6-5h12M-3-12v7M3-12v7" stroke="currentColor" strokeWidth="1" opacity=".5" />
        <rect x="-2.5" y="0" width="5" height="6" rx="1" fill="currentColor" />
        <circle cx="0" cy="2" r="1" fill="#fff" />
        <circle cx="11" cy="-8" r="1.5" fill="currentColor" className="glyph-event__orbit" />
      </g>
    );
  }
  if (cell.kind === 'risk') {
    return (
      <g className="glyph-crack" fill="none" stroke="currentColor" strokeLinejoin="round">
        <path d="M-10 8 4-11l3 9 6 5-9 2-4 6Z" strokeWidth="1.2" fill="currentColor" fillOpacity=".07" />
        <path d="M-3-8 2-1-3 3l6 4-4 5M3 0l7 1M-3 5l-6 3" strokeWidth="1.7" className="glyph-crack__line" />
        <circle cx="-5" cy="-3" r="1" fill="currentColor" stroke="none" className="glyph-crack__ember" />
        <circle cx="7" cy="7" r="1.2" fill="currentColor" stroke="none" className="glyph-crack__ember glyph-crack__ember--alt" />
      </g>
    );
  }
  return <path d="M0-3 3 0 0 3-3 0 0-3Z" fill="currentColor" opacity=".38" />;
}

function UnitSilhouette({ slot }: { slot: number }) {
  if (slot === 0) {
    return (
      <svg viewBox="0 0 44 54" className="unit-art unit-art--vector" aria-hidden="true">
        <path d="M17 36 4 28l5 16 8 4Z" fill="currentColor" opacity=".7" />
        <path d="M27 36 40 28l-5 16-8 4Z" fill="currentColor" opacity=".7" />
        <circle cx="22" cy="10" r="7" fill="currentColor" stroke="#0a2439" strokeWidth="1.6" />
        <path d="M15 8h14l-2 5H17l-2-5Z" fill="#fff" opacity=".85" />
        <path d="M17 18h10l5 15-6 5H18l-6-5 5-15Z" fill="currentColor" stroke="#0a2439" strokeWidth="1.6" />
        <path d="M16 22h12M19 38v11M25 38v11" stroke="#0a2439" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M14 26 7 36M30 26l7 10" stroke="#fff" strokeWidth="1.4" opacity=".55" />
        <circle cx="22" cy="25" r="3" fill="#fff" opacity=".8" />
      </svg>
    );
  }
  if (slot === 1) {
    return (
      <svg viewBox="0 0 44 54" className="unit-art unit-art--bastion" aria-hidden="true">
        <path d="M9 23 2 33l4 15 8 2 3-13Z" fill="currentColor" opacity=".75" />
        <path d="M35 23l7 10-4 15-8 2-3-13Z" fill="currentColor" opacity=".75" />
        <circle cx="22" cy="11" r="6" fill="currentColor" stroke="#0a2439" strokeWidth="1.6" />
        <path d="M16 9h12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" opacity=".85" />
        <path d="M13 20h18l6 18-8 9H15l-8-9 6-18Z" fill="currentColor" stroke="#0a2439" strokeWidth="1.8" />
        <path d="M17 25h10v14H17z" fill="#0a2439" opacity=".35" />
        <path d="M15 31h14" stroke="#fff" strokeWidth="1.5" opacity=".6" />
        <path d="M9 26h5v18H9z" fill="#fff" opacity=".3" />
        <circle cx="22" cy="30" r="3.2" fill="#fff" />
      </svg>
    );
  }
  if (slot === 2) {
    return (
      <svg viewBox="0 0 44 54" className="unit-art unit-art--orbit" aria-hidden="true">
        <ellipse cx="22" cy="29" rx="20" ry="8" fill="none" stroke="currentColor" strokeWidth="2" opacity=".65" />
        <circle cx="22" cy="25" r="13" fill="currentColor" stroke="#0a2439" strokeWidth="1.8" />
        <circle cx="22" cy="25" r="7" fill="#0a2439" opacity=".45" />
        <circle cx="22" cy="25" r="3.5" fill="#fff" />
        <path d="M8 20 2 14M36 20l6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <circle cx="2" cy="14" r="3" fill="currentColor" />
        <circle cx="42" cy="14" r="3" fill="currentColor" />
        <path d="M16 38 12 49h8l2-9M28 38l4 11h-8l-2-9" fill="currentColor" opacity=".8" />
        <path d="M18 25h8" stroke="#fff" strokeWidth="1.4" opacity=".7" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 44 54" className="unit-art unit-art--filo" aria-hidden="true">
      <path d="M22 2 37 18l-5 26-10 8-10-8-5-26L22 2Z" fill="currentColor" stroke="#0a2439" strokeWidth="1.6" />
      <path d="M22 8 30 19l-3 16-5 5-5-5-3-16 8-11Z" fill="#fff" opacity=".18" />
      <path d="M22 12 27 22l-5 8-5-8 5-10Z" fill="#fff" opacity=".8" />
      <path d="M10 25 2 31l7 3M34 25l8 6-7 3" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M16 42v9M28 42v9" stroke="#0a2439" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="22" cy="25" r="2" fill="#0a2439" />
    </svg>
  );
}

function computePieces(game: GameState): RenderPiece[] {
  const pieces: RenderPiece[] = [];
  const groups = new Map<string, RenderPiece[]>();

  for (const player of game.players) {
    for (let index = 0; index < player.pieces.length; index += 1) {
      const piece = player.pieces[index];
      if (!piece) continue;
      let x = 0;
      let y = 0;
      let key = `${player.id}-${piece.id}`;
      if (piece.status === 'home') {
        const point = baseSlotPoint(player.color, piece.slot);
        x = point.x;
        y = point.y;
      } else if (piece.status === 'goal') {
        const anchor = COLOR_META[player.color].goalAnchor;
        x = anchor.x;
        y = anchor.y;
        key = `goal:${player.color}`;
      } else {
        const ring = trackIndexForProgress(player.color, piece.progress);
        const point = RING_POSITIONS[ring ?? 0] ?? { x: 500, y: 500 };
        if (ring !== null) {
          x = point.x;
          y = point.y;
          key = `ring:${ring}`;
        } else {
          const lane = COLOR_META[player.color].lane[piece.progress - 64];
          x = lane?.x ?? point.x;
          y = lane?.y ?? point.y;
          key = `lane:${player.color}:${piece.progress}`;
        }
      }
      const entry: RenderPiece = { player, pieceIndex: index, legal: undefined, x, y };
      if (piece.status === 'active' || piece.status === 'goal') {
        const group = groups.get(key) ?? [];
        group.push(entry);
        groups.set(key, group);
      }
      pieces.push(entry);
    }
  }

  for (const [key, group] of groups) {
    if (group.length <= 1) continue;
    if (key.startsWith('goal:')) {
      const offsets = [
        [-13, -13],
        [13, -13],
        [-13, 13],
        [13, 13],
      ];
      group.forEach((entry, index) => {
        const offset = offsets[index] ?? [0, 0];
        entry.x += offset[0] ?? 0;
        entry.y += offset[1] ?? 0;
      });
    } else {
      group.forEach((entry, index) => {
        const angle = (Math.PI * 2 * index) / group.length - Math.PI / 2;
        entry.x += Math.cos(angle) * 8;
        entry.y += Math.sin(angle) * 8;
      });
    }
  }

  return pieces;
}

export function Board({ game, myPlayerId, onMove, animations, movingPieceId, safePieceId }: BoardProps) {
  const [inspected, setInspected] = useState<CellInfo | null>(null);
  const [tokens3dReady, setTokens3dReady] = useState(false);
  const current = game.players.find((player) => player.id === game.currentPlayerId);
  const myTurn = current?.id === myPlayerId && game.phase !== 'finished';
  const effectsActive = game.mode === 'adventure' && game.effectsEnabled;
  const legalByPiece = useMemo(() => {
    const map = new Map<string, LegalMove>();
    if (myTurn) for (const move of game.legalMoves) map.set(move.pieceId, move);
    return map;
  }, [game.legalMoves, myTurn]);
  const pieces = useMemo(() => {
    const computed = computePieces(game);
    return computed.map((entry) => ({
      ...entry,
      legal: legalByPiece.get(entry.player.pieces[entry.pieceIndex]?.id ?? ''),
    }));
  }, [game, legalByPiece]);
  const tokenPieces = useMemo<Token3DPiece[]>(
    () =>
      pieces.flatMap((entry) => {
        const piece = entry.player.pieces[entry.pieceIndex];
        if (!piece) return [];
        return [
          {
            key: `${entry.player.id}-${piece.id}`,
            color: entry.player.color,
            x: entry.x,
            y: entry.y,
            status: piece.status,
          },
        ];
      }),
    [pieces],
  );
  const inspectedInfo = inspected ? cellDescription(inspected, effectsActive) : null;
  const playerByColor = new Map(game.players.map((player) => [player.color, player]));

  return (
    <div className={`board-shell board-shell--nexus board-shell--altia ${animations ? '' : 'no-animations'}`}>
      <div className={`board board--nexus board--altia ${tokens3dReady ? 'has-3d-tokens' : ''}`} aria-label="Arena de Altia">
        <div className="board__platform-light" aria-hidden="true" />
        <svg viewBox="0 0 1000 1000" className="board__svg" role="img" aria-label="Arena de islas flotantes de Altia">
          <defs>
            <radialGradient id="arena-bg" cx="50%" cy="45%" r="70%">
              <stop offset="0" stopColor="#122244" />
              <stop offset=".5" stopColor="#081126" />
              <stop offset="1" stopColor="#030711" />
            </radialGradient>
            <linearGradient id="glass-panel" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity=".55" />
              <stop offset=".45" stopColor="#e8feff" stopOpacity=".18" />
              <stop offset="1" stopColor="#3a9ac8" stopOpacity=".18" />
            </linearGradient>
            <radialGradient id="core-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#ffffff" stopOpacity=".95" />
              <stop offset=".2" stopColor="#a8fff2" stopOpacity=".9" />
              <stop offset=".5" stopColor="#ff78d0" stopOpacity=".32" />
              <stop offset="1" stopColor="#ff6b73" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="core-orb" cx="42%" cy="38%" r="60%">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset=".22" stopColor="#b8fff5" />
              <stop offset=".52" stopColor="#48e6ff" />
              <stop offset=".8" stopColor="#ff79d2" />
              <stop offset="1" stopColor="#7b4dff" />
            </radialGradient>
            <linearGradient id="metal-rim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset=".2" stopColor="#a7e8ff" />
              <stop offset=".55" stopColor="#2d6c92" />
              <stop offset=".8" stopColor="#e6fbff" />
              <stop offset="1" stopColor="#4fa4c8" />
            </linearGradient>
            <pattern id="grid" width="34" height="34" patternUnits="userSpaceOnUse">
              <path d="M34 0H0v34" fill="none" stroke="#0b5778" strokeWidth=".7" opacity=".24" />
              <circle cx="0" cy="0" r="1.2" fill="#0b5778" opacity=".28" />
            </pattern>
            <pattern id="scanlines" width="3" height="3" patternUnits="userSpaceOnUse">
              <path d="M0 2.5h3" stroke="#ffffff" strokeWidth=".35" opacity=".22" />
            </pattern>
            {COLOR_ORDER.map((color) => {
              const values: Record<SeatColor, [string, string]> = {
                coral: ['#ff526b', '#5a0c24'],
                amber: ['#ffd35f', '#5b3b08'],
                jade: ['#4dffb2', '#08452f'],
                azure: ['#8b7bff', '#25115e'],
              };
              return (
                <linearGradient key={color} id={`dock-${color}`} x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor={values[color][0]} stopOpacity=".42" />
                  <stop offset=".45" stopColor={values[color][1]} stopOpacity=".3" />
                  <stop offset="1" stopColor="#ffffff" stopOpacity=".5" />
                </linearGradient>
              );
            })}
          </defs>

          <rect x="8" y="8" width="984" height="984" rx="34" fill="url(#arena-bg)" />
          <rect x="8" y="8" width="984" height="984" rx="34" fill="url(#grid)" />
          <rect x="8" y="8" width="984" height="984" rx="34" fill="url(#scanlines)" opacity=".45" />
          <rect x="18" y="18" width="964" height="964" rx="28" fill="none" stroke="url(#metal-rim)" strokeWidth="2.4" />
          <rect x="30" y="30" width="940" height="940" rx="22" fill="none" stroke="#ffffff" strokeOpacity=".65" />
          <rect x="42" y="42" width="916" height="916" rx="18" fill="url(#glass-panel)" opacity=".5" />

          <path d="M18 100 100 18M900 18l82 82M982 900l-82 82M100 982l-82-82" stroke="#0d4b68" strokeWidth="2.4" opacity=".45" />
          <path d="M34 128 128 34M872 34l94 94M966 872l-94 94M128 966l-94-94" stroke="#ffffff" strokeWidth="1.2" opacity=".7" />
          {[
            [76, 76],
            [924, 76],
            [924, 924],
            [76, 924],
          ].map(([x, y], index) => (
            <g key={index} transform={`translate(${x} ${y})`}>
              <path d="M-22 0h44M0-22v44" stroke="#0d4b68" strokeWidth="1.2" opacity=".6" />
              <path d="M-11 0 0 11 11 0 0-11Z" fill="none" stroke="#ffffff" strokeWidth="1.4" />
              <circle r="3" fill="#0d4b68" />
            </g>
          ))}

          <g className="arena-orbits" fill="none" stroke="#0d607f" pointerEvents="none">
            <circle cx="500" cy="500" r="334" strokeOpacity=".12" strokeDasharray="2 17" />
            <circle cx="500" cy="500" r="250" strokeOpacity=".1" strokeDasharray="1 11" />
            <path d="M176 500a324 324 0 0 1 648 0" strokeOpacity=".12" transform="rotate(35 500 500)" />
            <path d="M176 500a324 324 0 0 0 648 0" strokeOpacity=".12" transform="rotate(-35 500 500)" />
          </g>

          {COLOR_ORDER.map((color) => {
            const meta = COLOR_META[color];
            return (
              <g key={color} className={`dock dock--${color}`}>
                <rect
                  x={meta.base.x}
                  y={meta.base.y}
                  width={meta.base.width}
                  height={meta.base.height}
                  rx="28"
                  fill={`url(#dock-${color})`}
                  stroke={`var(--${color})`}
                  strokeOpacity=".55"
                  strokeWidth="1.4"
                />
                <rect
                  x={meta.base.x + 5}
                  y={meta.base.y + 5}
                  width={meta.base.width - 10}
                  height={meta.base.height - 10}
                  rx="24"
                  fill="none"
                  stroke={`var(--${color})`}
                  strokeOpacity=".16"
                />
                <path
                  d={`M${meta.base.x + 18} ${meta.base.y + 38}h${meta.base.width - 36}M${meta.base.x + 18} ${meta.base.y + meta.base.height - 32}h${meta.base.width - 36}`}
                  stroke={`var(--${color})`}
                  strokeOpacity=".25"
                  strokeWidth="1"
                />
                <g
                  transform={`translate(${meta.base.x + meta.base.width / 2} ${meta.base.y + meta.base.height / 2 + 14}) scale(2.3)`}
                  style={{ color: `var(--${color})` }}
                  opacity=".12"
                >
                  <FactionCrest sigil={meta.sigil} />
                </g>
                <path
                  d={`M${meta.base.x + 16} ${meta.base.y + 52}V${meta.base.y + 16}h36`}
                  fill="none"
                  stroke={`var(--${color})`}
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity=".65"
                />
                <path
                  d={`M${meta.base.x + meta.base.width - 16} ${meta.base.y + meta.base.height - 52}v36h-36`}
                  fill="none"
                  stroke={`var(--${color})`}
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity=".65"
                />
                {[0, 1, 2, 3].map((slot) => {
                  const point = baseSlotPoint(color, slot);
                  return (
                    <g key={slot} className="dock__pad">
                      <circle cx={point.x} cy={point.y} r="23" fill="#030812" fillOpacity=".72" stroke={`var(--${color})`} strokeOpacity=".5" />
                      <circle cx={point.x} cy={point.y} r="18" fill={`var(--${color})`} opacity=".08" />
                      <circle cx={point.x} cy={point.y} r="15" fill="none" stroke={`var(--${color})`} strokeOpacity=".18" strokeDasharray="2 4" />
                    </g>
                  );
                })}
              </g>
            );
          })}

          {COLOR_ORDER.map((color) => {
            const meta = COLOR_META[color];
            const start = meta.lane[0] ?? { x: 300, y: 300 };
            const end = meta.goalAnchor;
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const length = Math.hypot(dx, dy) || 1;
            const ux = dx / length;
            const uy = dy / length;
            return (
              <g key={`lane-${color}`} className={`energy-lane energy-lane--${color}`}>
                <path d={`M${start.x} ${start.y}L${end.x} ${end.y}`} stroke={`var(--${color})`} strokeOpacity=".1" strokeWidth="24" strokeLinecap="round" />
                <path d={`M${start.x} ${start.y}L${end.x} ${end.y}`} stroke={`var(--${color})`} strokeOpacity=".45" strokeWidth="6" strokeLinecap="round" />
                <path
                  d={`M${start.x} ${start.y}L${end.x} ${end.y}`}
                  stroke="#ffffff"
                  strokeOpacity=".65"
                  strokeWidth="1.4"
                  strokeDasharray="10 18"
                  className="energy-lane__pulse"
                />
                {meta.lane.map((point, index) => {
                  const size = 11 + index * 0.5;
                  return (
                    <g key={index} transform={`translate(${point.x} ${point.y})`} className="energy-lane__node">
                      <rect x={-size} y={-size} width={size * 2} height={size * 2} rx="4" fill="#07172f" fillOpacity=".9" stroke={`var(--${color})`} strokeOpacity=".7" />
                      <path
                        d={`M${-ux * 7 - uy * 4} ${-uy * 7 + ux * 4}L${ux * 5} ${uy * 5}L${-ux * 7 + uy * 4} ${-uy * 7 - ux * 4}`}
                        fill="none"
                        stroke={`var(--${color})`}
                        strokeWidth="1.5"
                        opacity=".8"
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}

          <g className="core" aria-hidden="true">
            <circle cx="500" cy="500" r="160" fill="url(#core-glow)" opacity=".55" />
            <circle cx="500" cy="500" r="116" fill="#030812" stroke="url(#metal-rim)" strokeWidth="3" />
            <circle cx="500" cy="500" r="104" fill="none" stroke="#74efff" strokeOpacity=".35" />
            <g className="core__rotor">
              <circle cx="500" cy="500" r="91" fill="none" stroke="#69efff" strokeOpacity=".55" strokeWidth="1.2" strokeDasharray="18 9 3 9" />
              <circle cx="500" cy="500" r="82" fill="none" stroke="#7b5cff" strokeOpacity=".6" strokeWidth="2" strokeDasharray="2 12" />
              {Array.from({ length: 4 }, (_, index) => {
                const angle = (Math.PI / 2) * index;
                return (
                  <path
                    key={index}
                    d={`M${500 + Math.cos(angle) * 75} ${500 + Math.sin(angle) * 75} A75 75 0 0 1 ${500 + Math.cos(angle + 0.8) * 75} ${500 + Math.sin(angle + 0.8) * 75}`}
                    fill="none"
                    stroke="#8bf7ff"
                    strokeWidth="2.2"
                    opacity=".55"
                  />
                );
              })}
            </g>
            <g className="core__swirl">
              <path d="M500 445c34 0 62 25 62 55s-26 55-62 55-62-25-62-55c0-18 10-34 27-44-8 16-7 30 5 39 15 12 40 7 52-10 10-15 4-32-12-40-4-2-7-3-10-3Z" fill="url(#core-orb)" />
              <path d="M500 555c-34 0-62-25-62-55s26-55 62-55 62 25 62 55c0 18-10 34-27 44 8-16 7-30-5-39-15-12-40-7-52 10-10 15-4 32 12 40 4 2 7 3 10 3Z" fill="#6cf6ff" opacity=".14" />
            </g>
            <circle cx="500" cy="500" r="35" fill="url(#core-orb)" className="core__orb" />
            <circle cx="500" cy="500" r="48" fill="none" stroke="#d8ffff" strokeOpacity=".4" strokeDasharray="1 6" />
            <path d="M500 398v18M500 584v18M398 500h18M584 500h18" stroke="#8cefff" strokeWidth="2" opacity=".5" />
            <text x="500" y="632" textAnchor="middle" fill="#8cefff" fontSize="9" fontWeight="700" letterSpacing="5">
              NÚCLEO
            </text>
          </g>

          {CELLS.map((cell) => {
            const point = RING_POSITIONS[cell.index];
            if (!point) return null;
            const isEffect = cell.kind === 'shortcut' || cell.kind === 'event' || cell.kind === 'risk';
            const disabled = isEffect && !effectsActive;
            return (
              <g
                key={cell.index}
                className={`track-cell track-cell--${cell.kind} ${cell.color ? `track-cell--${cell.color}` : ''} ${disabled ? 'is-disabled' : ''}`}
                transform={`translate(${point.x - RING_CELL_SIZE / 2} ${point.y - RING_CELL_SIZE / 2})`}
                role="button"
                tabIndex={0}
                aria-label={cellDescription(cell, effectsActive).title}
                onClick={() => setInspected(cell)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setInspected(cell);
                  }
                }}
              >
                <rect width={RING_CELL_SIZE} height={RING_CELL_SIZE} rx="7" className="track-cell__rect" />
                <rect x="2.5" y="2.5" width={RING_CELL_SIZE - 5} height={RING_CELL_SIZE - 5} rx="5" className="track-cell__inset" />
                <path d={`M6 ${RING_CELL_SIZE - 6}h7M${RING_CELL_SIZE - 13} 6h7`} stroke="currentColor" strokeWidth=".9" opacity=".3" />
                <g transform={`translate(${RING_CELL_SIZE / 2} ${RING_CELL_SIZE / 2})`} className="track-cell__glyph">
                  <CellGlyph cell={cell} />
                </g>
              </g>
            );
          })}
        </svg>

        <Token3DLayer pieces={tokenPieces} animations={animations} onReady={setTokens3dReady} />

        <div className="board__atmosphere" aria-hidden="true">
          {Array.from({ length: 18 }, (_, index) => (
            <span
              key={index}
              style={{
                left: `${6 + ((index * 47) % 88)}%`,
                top: `${10 + ((index * 61) % 80)}%`,
                animationDelay: `${(index % 8) * 0.65}s`,
                animationDuration: `${4.5 + (index % 5)}s`,
                ['--mote-x' as string]: `${(index % 2 === 0 ? 1 : -1) * (10 + (index % 4) * 6)}px`,
              }}
            />
          ))}
        </div>

        <div className="board__bases" aria-hidden="true">
          {COLOR_ORDER.map((color) => {
            const meta = COLOR_META[color];
            const player = playerByColor.get(color);
            return (
              <div
                key={color}
                className={`base-banner-wrap base-banner-wrap--${color} ${player ? '' : 'is-empty'}`}
                style={{
                  left: `${(meta.base.x + meta.base.width / 2) / 10}%`,
                  top: `${(meta.base.y + meta.base.height / 2) / 10}%`,
                  width: `${meta.base.width / 10}%`,
                  height: `${meta.base.height / 10}%`,
                }}
              >
                {player && (
                  <div className="base-watermark">
                    <Avatar avatar={player.avatar} size={138} decorative />
                  </div>
                )}
                <div className="base-banner">
                  {player ? (
                    <Avatar avatar={player.avatar} size={27} decorative />
                  ) : (
                    <span className="base-banner__crest">
                      <svg viewBox="-14 -14 28 28" width="24" height="24">
                        <FactionCrest sigil={meta.sigil} />
                      </svg>
                    </span>
                  )}
                  <span>
                    <strong>{player?.name ?? meta.house}</strong>
                    <small>{player ? meta.house : 'Muelle libre'}</small>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="board__pieces">
          {pieces.map((entry) => {
            const piece = entry.player.pieces[entry.pieceIndex];
            if (!piece) return null;
            const canMove = Boolean(entry.legal);
            const isMoving = movingPieceId === piece.id;
            const label = `${unitRole(piece.slot)} de ${entry.player.name}. ${
              piece.status === 'home'
                ? 'En el muelle'
                : piece.status === 'goal'
                  ? 'En el Núcleo'
                  : piece.progress >= 64
                    ? `Conducto ${piece.progress - 63}/7`
                    : 'En la arena'
            }.${canMove ? ' Movimiento disponible.' : ''}`;
            return (
              <button
                key={`${entry.player.id}-${piece.id}`}
                type="button"
                className={`piece piece--${entry.player.color} ${canMove ? 'is-selectable' : ''} ${
                  piece.shielded ? 'is-shielded' : ''
                } ${piece.status === 'goal' ? 'is-goal' : ''} ${piece.status === 'home' ? 'is-home' : ''} ${
                  game.currentPlayerId === entry.player.id ? 'is-current' : ''
                } ${isMoving ? 'is-moving' : ''} ${safePieceId === piece.id ? 'is-safe-landing' : ''}`}
                style={{
                  left: `${entry.x / 10}%`,
                  top: `${entry.y / 10}%`,
                  transitionDuration: animations ? undefined : '0ms',
                }}
                onClick={() => {
                  if (canMove) onMove(piece.id);
                }}
                disabled={!canMove}
                aria-label={label}
                title={label}
              >
                <span className="piece__trail" aria-hidden="true" />
                <span className="piece__body">
                  <span className="piece__glass" />
                  <span className="piece__core" />
                  <span className="piece__ring" />
                  <span className="piece__sigil">{['⌁', '✧', '◇', '⬡'][piece.slot] ?? '◇'}</span>
                  <span className="piece__number">{piece.slot + 1}</span>
                  {piece.shielded && (
                    <span className="piece__shield">
                      <Icon name="shield" size={11} />
                    </span>
                  )}
                </span>
                <span className="piece__plinth" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        {inspectedInfo && (
          <div className="cell-info" role="status">
            <div className="cell-info__icon">
              <Icon
                name={
                  inspected?.kind === 'safe'
                    ? 'shield'
                    : inspected?.kind === 'risk'
                      ? 'rift'
                      : inspected?.kind === 'shortcut'
                        ? 'portal'
                        : inspected?.kind === 'event'
                          ? 'spark'
                          : 'flag'
                }
                size={18}
              />
            </div>
            <div>
              <strong>{inspectedInfo.title}</strong>
              <p>{inspectedInfo.text}</p>
            </div>
            <button type="button" className="icon-button icon-button--small" onClick={() => setInspected(null)} aria-label="Cerrar información">
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="board-legend" aria-label="Leyenda de la arena">
        <span><i className="legend-dot legend-dot--start" />Salida segura</span>
        <span><i className="legend-dot legend-dot--safe" />Zona segura</span>
        <span><i className="legend-dot legend-dot--shortcut" />Portal</span>
        <span><i className="legend-dot legend-dot--event" />Cofre</span>
        <span><i className="legend-dot legend-dot--risk" />Fisura</span>
      </div>
    </div>
  );
}
